// src/features/auth/authSlice.ts
import { Billing, GetMeResponse } from "@hz/stealthy-gmc-cloaker-common/api-types/auth";
import { OnboardingStepType, OurRoles, PlanNamesType} from "@hz/stealthy-gmc-cloaker-common/constants";
import { createSlice, PayloadAction, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import { api } from "@state/apiSlice";
import logger from "@utils/logger";

interface AuthState {
  token: string | null;
  isLoading: boolean;
  userId: string | null;
  userRole: OurRoles | null;
  onboardingStep: OnboardingStepType | null;
  accountId: string | null;
  error: FetchError | null;
  billing: Billing | null;
}

const initialState: AuthState = {
  token: localStorage.getItem("token"),
  isLoading: false,
  onboardingStep: null,
  userId: null,
  userRole: null,
  billing: null,
  accountId: null,
  error: null,
};

type FetchError = { message: string; status: number };

export const fetchInit = createAsyncThunk<GetMeResponse, { forceRefetch?: boolean }, { rejectValue: FetchError }>(
  "stealthygmc/fetchInit",
  async ({ forceRefetch = false }, { getState, dispatch, rejectWithValue }) => {
    try {
      const resp = await dispatch(api.endpoints.init.initiate({}, { forceRefetch })).unwrap();

      return resp;
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue({ message: error.message, status: 500 });
      } else if (typeof error === "string") {
        return rejectWithValue({ message: error, status: 500 });
      } else {
        return rejectWithValue({ message: "An unknown error occurred", status: 500 });
      }
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<{ token: string; email: string }>) => {
      state.token = action.payload.token;

      localStorage.setItem("token", action.payload.token);
    },
    logout: (state) => {
      state.token = null;
      state.userId = null;
      state.accountId = null;
      state.error = null;
      localStorage.removeItem("token");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInit.pending, (state) => {
        logger.debug("[fetchInit]: Pending...");
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInit.fulfilled, (state, action: PayloadAction<GetMeResponse>) => {
        logger.debug("[fetchInit] Fulfilled with payload:", action.payload);
        (state.accountId = action.payload.accountId), 
        (state.onboardingStep = action.payload.onboardingStep),
        (state.userId = action.payload.userId),
        (state.userRole = action.payload.role),
        (state.billing = action.payload.billing),
        (state.isLoading = false);
        logger.debug("[fetchInit] Updated state.accountId to:", state.accountId);
      })
      .addCase(fetchInit.rejected, (state, action) => {
        logger.error("[fetchInit] Rejected with error:", action.payload);
        state.isLoading = false;
        state.error = action.payload as FetchError;
      });
  },
});

export const selectUserState = (state: { user: AuthState }) => state.user;

export const selectBillingState = (state: { billing: AuthState }) => state.billing;

export const selectAccountId = (state: { user: AuthState }) => state.user.accountId;

export const { setCredentials, logout } = userSlice.actions;

export default userSlice.reducer;
